import { userProfileSchema } from "~/schemas/user";
import { sessionSchema, fullSessionSchema } from "~/schemas/agent";
import { featureFlagSchema, globalMessageSchema } from "~/schemas/admin";
import type { FeatureFlag, GlobalMessage } from "~/schemas/admin";
import {
  sessionRatingSchema,
  sessionRatingUpsertSchema,
} from "~/schemas/rating";
import { z } from "zod";
import { getCookie } from "~/utils";
import * as Sentry from "@sentry/react";
import { redirect } from "react-router";

export class ApiError extends Error {
  public statusCode: number;
  public details: string;

  constructor(message: string, statusCode: number, details: string) {
    super(message);
    this.statusCode = statusCode;
    this.details = details;
  }
}

export default class Client {
  private baseUrl: string = "/api";
  private handle404: boolean = true;
  private handle401And403: boolean = true;

  constructor(handle404: boolean = true, handle401And403: boolean = true) {
    this.handle404 = handle404;
    this.handle401And403 = handle401And403;
  }

  async getSessions() {
    const response = await this.fetchData("agent/sessions");
    return sessionSchema.array().parse(response);
  }

  async getSession(sessionId: string) {
    const response = await this.fetchData(`agent/sessions/${sessionId}`);
    return fullSessionSchema.parse(response);
  }

  async ask(sessionId: string, question: string, signal: AbortSignal) {
    const payload = { question };
    return await this.fetchData(`agent/sessions/${sessionId}/ask`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      signal,
    });
  }

  async anonLogin() {
    return await this.fetchData("auth/anon-sessions");
  }

  async userProfile() {
    const response = await this.fetchData("auth/sessions/me");
    return userProfileSchema.parse(response);
  }

  async login(redirectTo: string = "/") {
    const searchParams = new URLSearchParams({ redirect_to: redirectTo });
    return await this.fetchData(`auth/sessions?${searchParams}`);
  }

  async logout(redirectTo: string = "/") {
    const searchParams = new URLSearchParams({
      redirect_to: redirectTo,
    });
    return await this.fetchData(`auth/sessions?${searchParams}`, {
      method: "DELETE",
    });
  }

  async getFeatureFlag(id: number) {
    const response = await this.fetchData(`admin/feature-flags/${id}`);
    return featureFlagSchema.parse(response);
  }

  async getFeatureFlags() {
    const response = await this.fetchData("admin/feature-flags");
    return z.array(featureFlagSchema).parse(response);
  }

  async updateFeatureFlag(
    id: number,
    payload: Pick<FeatureFlag, "allowed_for_domains" | "allowed_for_user_ids">
  ) {
    const response = await this.fetchData(`admin/feature-flags/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    return featureFlagSchema.parse(response);
  }

  async getEnabledGlobalMessages() {
    const response = await this.fetchData("global-messages");
    return z.array(globalMessageSchema).parse(response);
  }

  async getGlobalMessage(id: number) {
    const response = await this.fetchData(`admin/global-messages/${id}`);
    return globalMessageSchema.parse(response);
  }

  async getGlobalMessages() {
    const response = await this.fetchData("admin/global-messages");
    return z.array(globalMessageSchema).parse(response);
  }

  async createGlobalMessage(payload: Pick<GlobalMessage, "title" | "content">) {
    const response = await this.fetchData(`admin/global-messages`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    return globalMessageSchema.parse(response);
  }

  async deleteGlobalMessage(id: number) {
    await this.fetchData(`admin/global-messages/${id}`, {
      method: "DELETE",
    });
    return null;
  }

  async updateGlobalMessage(
    id: number,
    payload: Pick<GlobalMessage, "title" | "content">
  ) {
    const response = await this.fetchData(`admin/global-messages/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    return globalMessageSchema.parse(response);
  }

  async triggerDryDataSync(datasets: string[] | "all" = "all") {
    const payload = { datasets };
    const response = await this.fetchData("admin/trigger-dry-data-sync", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    return z.string().array().parse(response);
  }

  async getSessionRating(sessionId: string) {
    const response = await this.fetchData(`agent/sessions/${sessionId}/rating`);
    if (!response) return null;
    try {
      return sessionRatingSchema.parse(response);
    } catch {
      return null;
    }
  }

  async upsertSessionRating(
    sessionId: string,
    payload: z.infer<typeof sessionRatingUpsertSchema>
  ) {
    const response = await this.fetchData(
      `agent/sessions/${sessionId}/rating`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }
    );
    return sessionRatingSchema.parse(response);
  }

  async getSessionsForAnalyticsReport(filters: {
    min_messages_count: number;
    since_last_days: number;
  }) {
    const searchParams = new URLSearchParams({
      min_messages_count: filters.min_messages_count.toString(),
      since_last_days: filters.since_last_days.toString(),
    });
    const response = await this.fetchData(
      `admin/reports/sessions-for-analytics${searchParams.toString() ? "?" : ""}${searchParams.toString()}`
    );
    return sessionSchema.array().parse(response);
  }

  async fetchData(endpoint: string, options: RequestInit | null = null) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60000 * 10);

    let optionsWithDefaults: RequestInit = {
      method: "GET",
      credentials: "include",
      signal: controller.signal,
    };
    if (options) {
      optionsWithDefaults = { ...optionsWithDefaults, ...options };
    }

    const csrf = getCookie("XSRF-TOKEN");
    let headers = { "X-XSRF-TOKEN": csrf || "" };

    if (optionsWithDefaults.headers) {
      headers = { ...headers, ...optionsWithDefaults.headers };
    }
    optionsWithDefaults.headers = headers;

    try {
      const response = await fetch(
        `${this.baseUrl}/${endpoint}`,
        optionsWithDefaults
      );
      if (response.redirected) {
        window.location.href = response.url;
      } else if (!response.ok) {
        if (response.status === 404 && this.handle404) {
          throw redirect("/404");
        }
        if (
          (response.status === 401 || response.status === 403) &&
          this.handle401And403
        ) {
          throw redirect("/404");
        }

        throw new ApiError(
          `API Error at ${endpoint}`,
          response.status,
          response.statusText
        );
      }

      const contentType = response.headers.get("Content-Type");
      if (
        contentType?.includes("application/octet-stream") ||
        contentType?.includes("text/event-stream")
      ) {
        return response.body; // Return the readable stream
      }
      if (contentType?.includes("application/json")) {
        return response.json();
      }
      return response.text();
    } catch (err: any) {
      if (err.name === "AbortError") {
        console.log("Fetch aborted by user");
      } else {
        Sentry.captureException(err, {
          extra: { endpoint, options: optionsWithDefaults },
        });
        throw err;
      }
    } finally {
      clearTimeout(timeoutId);
    }
  }
}
